import { useRef, useState } from "react";
import { FileText, ImagePlus, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, mediaUrl } from "../../lib/api";

const useUpload = () => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      return data;
    } catch (err) {
      toast.error(formatError(err));
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { busy, upload };
};

export const ImageField = ({ value, onChange, testId }) => {
  const ref = useRef(null);
  const { busy, upload } = useUpload();
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const res = await upload(file);
    if (res) onChange(res.url);
  };
  return (
    <div className="flex items-start gap-4">
      <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-md border border-dashed border-teal/30 bg-mist">
        {value ? <img src={mediaUrl(value)} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-6 w-6 text-teal/50" />}
      </div>
      <div className="flex flex-1 flex-col gap-2">
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testId}-upload-btn`} className="inline-flex items-center gap-2 self-start rounded-full bg-teal px-4 py-2 text-xs text-mist hover:bg-teal-ink disabled:opacity-60">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload image
        </button>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste an image link" data-testid={`${testId}-url`} className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm outline-none focus:border-teal" />
        {value && (
          <button type="button" onClick={() => onChange("")} className="self-start text-xs text-red-700 hover:underline" data-testid={`${testId}-remove`}>
            Remove image
          </button>
        )}
      </div>
    </div>
  );
};

export const FilesField = ({ value, onChange, testId }) => {
  const ref = useRef(null);
  const { busy, upload } = useUpload();
  const list = Array.isArray(value) ? value : [];
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const res = await upload(file);
    if (res) onChange([...list, { name: res.name, url: res.url }]);
  };
  const rename = (i, name) => onChange(list.map((a, j) => (j === i ? { ...a, name } : a)));
  return (
    <div className="space-y-2">
      {list.map((a, i) => (
        <div key={a.url + i} className="flex items-center gap-2 rounded-md border border-input bg-white px-3 py-2">
          <FileText className="h-4 w-4 shrink-0 text-teal" />
          <input value={a.name} onChange={(e) => rename(i, e.target.value)} className="flex-1 bg-transparent text-sm outline-none" data-testid={`${testId}-name-${i}`} />
          <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} aria-label="Remove file" data-testid={`${testId}-remove-${i}`}>
            <Trash2 className="h-4 w-4 text-red-700" />
          </button>
        </div>
      ))}
      <input ref={ref} type="file" accept="application/pdf,image/*" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testId}-upload-btn`} className="inline-flex items-center gap-2 rounded-full border border-teal px-4 py-2 text-xs text-teal hover:bg-teal hover:text-mist disabled:opacity-60">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload file (PDF or image)
      </button>
    </div>
  );
};
